// Mirror of app/services/branding/palette_generator.rb.
// Both implementations MUST produce identical outputs so server-rendered
// SCSS variables match client-rendered CSS variables exactly.

const STEPS = {
  25: 0.96,
  50: 0.9,
  75: 0.82,
  100: 0.72,
  200: 0.55,
  300: 0.38,
  400: 0.18,
  500: 0.0,
  600: -0.1,
  700: -0.22,
  800: -0.45,
  900: -0.7,
};

const clamp = v => Math.min(1, Math.max(0, v));

const normalize = hex => {
  let h = String(hex || '').trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  return `#${h.toLowerCase()}`;
};

const hexToRgb = hex =>
  hex
    .replace('#', '')
    .match(/../g)
    .map(c => parseInt(c, 16) / 255);

const rgbToHsl = (r, g, b) => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h / 6, s, l];
};

const hueToRgb = (p, q, t) => {
  let x = t;
  if (x < 0) x += 1;
  if (x > 1) x -= 1;
  if (x < 1 / 6) return p + (q - p) * 6 * x;
  if (x < 1 / 2) return q;
  if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
  return p;
};

const hslToRgb = (h, s, l) => {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hueToRgb(p, q, h + 1 / 3), hueToRgb(p, q, h), hueToRgb(p, q, h - 1 / 3)];
};

const toHex = n => Math.round(n * 255).toString(16).padStart(2, '0');

const hslToHex = (h, s, l) => {
  const [r, g, b] = hslToRgb(h, s, l);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const buildPalette = inputHex => {
  const hex = normalize(inputHex);
  const [h, s, l] = rgbToHsl(...hexToRgb(hex));
  const palette = {};
  Object.entries(STEPS).forEach(([key, delta]) => {
    const newL = delta > 0 ? l + (1 - l) * delta : l + l * delta;
    palette[key] = hslToHex(h, s, clamp(newL));
  });
  return palette;
};

export const hexToRgbTriplet = hex => {
  const [r, g, b] = hexToRgb(normalize(hex));
  return `${Math.round(r * 255)} ${Math.round(g * 255)} ${Math.round(b * 255)}`;
};
